import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge } from "../components/Shared";
import { toast } from "sonner";
import {
  UserPlus, Trophy, BookOpen, Sparkle, ArrowRight, VideoCamera,
  Handshake, Certificate, Compass, Warning,
} from "@phosphor-icons/react";

const STAGE_META = {
  applicant: { label: "Postulante", tone: "neutral" },
  filtered_out: { label: "Filtrado", tone: "signal" },
  scored: { label: "Evaluado", tone: "neutral" },
  priority_high: { label: "Prioridad alta", tone: "terracotta" },
  priority_medium: { label: "Prioridad media", tone: "warning" },
  future_base: { label: "Base futura", tone: "neutral" },
  video_pending: { label: "Video pendiente", tone: "warning" },
  video_received: { label: "Video recibido", tone: "forest" },
  interview_pending: { label: "Entrevista pendiente", tone: "warning" },
  interview_approved: { label: "Entrevista aprobada", tone: "forest" },
  interview_rejected: { label: "Entrevista rechazada", tone: "signal" },
  agreement_pending: { label: "Acuerdo pendiente", tone: "warning" },
  signed: { label: "Firmado", tone: "forest" },
  induction_pending: { label: "Inducción pendiente", tone: "warning" },
  induction_completed: { label: "Inducción completada", tone: "forest" },
  ambassador_active: { label: "Activo", tone: "terracotta" },
  ambassador_inactive: { label: "Inactivo", tone: "signal" },
  ambassador_removed: { label: "Removido", tone: "signal" },
};

const TYPE_LABELS = {
  A_territorial: "A · Territorial",
  B_content: "B · Contenido",
  C_community: "C · Comunidad",
  D_operative: "D · Operativo",
};

export default function AmbassadorDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCapture, setShowCapture] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ambassador/dashboard");
      setData(data);
    } catch (e) { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runInactivityCheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ambassador/inactivity/check");
      toast.success(`Inactividad: ${data.checked} revisados · ${data.moved_to_inactive} marcados inactivos`);
      load();
    } catch (e) { toast.error("Error"); } finally { setBusy(false); }
  };

  const advance = async (userId, target_stage_key, reason) => {
    try {
      await api.post(`/ambassador/advance/${userId}`, { target_stage_key, reason: reason || "manual" });
      toast.success(`Stage → ${target_stage_key}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const requestVideo = async (userId) => {
    try {
      await api.post(`/ambassador/video/request/${userId}`);
      toast.success("Video solicitado por WhatsApp");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const scheduleInterview = async (userId) => {
    try {
      await api.post(`/ambassador/interview/schedule/${userId}`);
      toast.success("Entrevista agendada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const submitInterview = async (userId, decision) => {
    try {
      const { data } = await api.post(`/ambassador/interview/submit/${userId}`, { decision });
      toast.success(`Entrevista · ${data.stage}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const sendAgreement = async (userId) => {
    try {
      await api.post(`/ambassador/agreement/send/${userId}`);
      toast.success("Acuerdo enviado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (loading || !data) return <div className="p-10 text-carbon-500">Cargando...</div>;

  if (data.error === "pipeline_missing") {
    return (
      <PageBody>
        <Card className="p-10 text-center max-w-xl mx-auto mt-10">
          <div className="section-label mb-2">Ambassador Pipeline · sin inicializar</div>
          <h2 className="font-heading text-2xl mb-3">No hay pipeline ambassador en la base de datos</h2>
          <p className="text-sm text-carbon-600 leading-relaxed mb-4">
            Esto pasa si borraste toda la data. Re-corre el seed que crea los pipelines, agentes IA y stages:
          </p>
          <pre className="bg-page border border-carbon-200 rounded p-3 text-xs font-mono text-left mb-3">cd /app/backend && python3 seed.py</pre>
          <p className="text-xs text-carbon-500">O contacta al admin para que lo ejecute en producción.</p>
        </Card>
      </PageBody>
    );
  }
  if (data.error) return <div className="p-10 text-signal">Error: {data.error}. Ejecuta seed/reset desde Configuración.</div>;

  const counts = data.stage_counts || {};
  const orderedKeys = Object.keys(STAGE_META);

  return (
    <>
      <PageHeader
        label="Origin-side · captación territorial"
        title="Ambassador Pipeline"
        description="Embajadores territoriales en su país de origen. Score 0-100, tipos A/B/C/D, video, entrevista, acuerdo, inducción y check-ins biweekly."
      >
        <button onClick={runInactivityCheck} disabled={busy} data-testid="check-inactivity-btn" className="bg-carbon-200 text-carbon-900 px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-carbon-300 disabled:opacity-60">
          <Warning size={14} /> Chequear inactividad
        </button>
        <button onClick={() => setShowCapture(true)} data-testid="open-capture-btn" className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover">
          <UserPlus size={14} /> Capturar postulante
        </button>
      </PageHeader>

      <PageBody>
        {/* Funnel 18 stages */}
        <Card className="p-6 mb-6" testid="ambassador-stage-funnel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="section-label">Funnel · 18 stages</div>
              <h2 className="font-heading text-xl">Distribución · {data.total_in_pipeline} en pipeline</h2>
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-2">
            {orderedKeys.map((key) => {
              const meta = STAGE_META[key];
              const count = counts[key] || 0;
              return (
                <div key={key} className="border border-carbon-200 rounded p-3 hover:border-terracotta transition-colors" data-testid={`stage-${key}`}>
                  <div className="text-[10px] uppercase tracking-wider text-carbon-500 mb-1">{meta.label}</div>
                  <div className="font-heading text-2xl font-light tracking-tighter">{count}</div>
                </div>
              );
            })}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Tipos A/B/C/D */}
          <Card className="p-6" testid="ambassador-types">
            <div className="flex items-center gap-2 mb-4">
              <Compass size={18} className="text-terracotta" />
              <div>
                <div className="section-label">Tipos</div>
                <h2 className="font-heading text-xl">Embajadores por perfil</h2>
              </div>
            </div>
            <div className="space-y-2">
              {Object.entries(data.by_type || {}).map(([t, n]) => (
                <div key={t} className="flex items-center justify-between p-3 border border-carbon-200 rounded">
                  <div className="text-sm font-medium">{TYPE_LABELS[t] || t}</div>
                  <div className="font-mono text-lg text-terracotta">{n}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top municipios */}
          <Card className="p-6 lg:col-span-2" testid="ambassador-top-territories">
            <div className="section-label mb-1">Territorios activos</div>
            <h2 className="font-heading text-xl mb-4">Embajadores activos por municipio</h2>
            {(!data.by_municipality || data.by_municipality.length === 0) ? (
              <div className="text-sm text-carbon-400">Aún no hay embajadores activos.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.by_municipality.map((t, idx) => (
                  <div key={idx} className="flex items-center justify-between border border-carbon-200 rounded p-3">
                    <div>
                      <div className="font-medium text-sm">{t.municipality}</div>
                      <div className="text-xs text-carbon-500">{t.country}</div>
                    </div>
                    <div className="font-mono text-lg text-terracotta">{t.active}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Prioridad alta */}
        <Card className="p-6 mb-6" testid="ambassador-priority-high">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={18} className="text-terracotta" />
            <div>
              <div className="section-label">Score ≥ 70</div>
              <h2 className="font-heading text-xl">Prioridad alta · siguiente paso video</h2>
            </div>
          </div>
          <ApplicantsTable users={data.applicants_priority_high} onRequestVideo={requestVideo} onAdvance={advance} />
        </Card>

        {/* Prioridad media */}
        <Card className="p-6 mb-6" testid="ambassador-priority-medium">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={18} className="text-warning" />
            <div>
              <div className="section-label">Score 55-69</div>
              <h2 className="font-heading text-xl">Prioridad media</h2>
            </div>
          </div>
          <ApplicantsTable users={data.applicants_priority_medium} onRequestVideo={requestVideo} onAdvance={advance} />
        </Card>

        {/* Interview queue */}
        <Card className="p-6 mb-6" testid="ambassador-interview-queue">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={18} className="text-forest" />
            <div>
              <div className="section-label">Entrevistas</div>
              <h2 className="font-heading text-xl">Cola pendiente</h2>
            </div>
          </div>
          {(!data.interview_queue || data.interview_queue.length === 0) ? (
            <div className="text-sm text-carbon-400 py-4">Sin entrevistas pendientes.</div>
          ) : (
            <div className="space-y-2">
              {data.interview_queue.map((u) => (
                <div key={u.id} className="flex items-center gap-4 p-3 border border-carbon-200 rounded">
                  <div className="flex-1 min-w-0">
                    <Link to={`/users/${u.id}`} className="font-medium text-sm hover:text-terracotta">{u.name}</Link>
                    <div className="text-xs text-carbon-500">{u.origin_country} · {u.origin_municipality}</div>
                  </div>
                  <Badge tone="terracotta">{u.ambassador_score}/100</Badge>
                  <button onClick={() => submitInterview(u.id, "approved")} className="text-xs bg-forest text-white px-3 py-1.5 rounded hover:opacity-90" data-testid={`interview-approve-${u.public_id}`}>Aprobar</button>
                  <button onClick={() => submitInterview(u.id, "rejected")} className="text-xs bg-signal text-white px-3 py-1.5 rounded hover:opacity-90" data-testid={`interview-reject-${u.public_id}`}>Rechazar</button>
                  <button onClick={() => sendAgreement(u.id)} className="text-xs bg-carbon-200 px-3 py-1.5 rounded hover:bg-carbon-300" data-testid={`send-agreement-${u.public_id}`}>Enviar acuerdo</button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Active ambassadors */}
        <Card className="p-6" testid="ambassador-active-list">
          <div className="flex items-center gap-2 mb-4">
            <Handshake size={18} className="text-terracotta" />
            <div>
              <div className="section-label">Operación</div>
              <h2 className="font-heading text-xl">Embajadores activos</h2>
            </div>
          </div>
          {(!data.active_ambassadors || data.active_ambassadors.length === 0) ? (
            <div className="text-sm text-carbon-400 py-4">Aún no hay embajadores activos.</div>
          ) : (
            <div className="space-y-2">
              {data.active_ambassadors.map((u) => (
                <Link key={u.id} to={`/users/${u.id}`} className="flex items-center gap-4 p-3 border border-carbon-200 rounded hover:border-terracotta">
                  <Certificate size={20} className="text-terracotta" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{u.name}</div>
                    <div className="text-xs text-carbon-500 truncate">{u.origin_country} · {u.origin_municipality}</div>
                  </div>
                  {u.validated_type && <Badge tone="forest">{TYPE_LABELS[u.validated_type] || u.validated_type}</Badge>}
                  <Badge tone="terracotta">{u.ambassador_score}/100</Badge>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </PageBody>

      {showCapture && <AmbassadorCaptureModal onClose={() => setShowCapture(false)} onCreated={() => { setShowCapture(false); load(); }} />}
    </>
  );
}

function ApplicantsTable({ users, onRequestVideo, onAdvance }) {
  if (!users || users.length === 0) {
    return <div className="text-sm text-carbon-400 py-4">Sin postulantes en este nivel.</div>;
  }
  return (
    <div className="space-y-2">
      {users.map((u) => (
        <div key={u.id} className="flex items-center gap-4 p-3 border border-carbon-200 rounded hover:border-terracotta transition-colors">
          <div className="flex-1 min-w-0">
            <Link to={`/users/${u.id}`} className="font-medium text-sm hover:text-terracotta">{u.name}</Link>
            <div className="text-xs text-carbon-500 truncate">{u.origin_country} · {u.origin_department} · {u.origin_municipality}</div>
          </div>
          {u.primary_type && <Badge tone="neutral">{TYPE_LABELS[u.primary_type] || u.primary_type}</Badge>}
          <div className="text-right">
            <div className="text-[10px] text-carbon-500">Score</div>
            <div className="font-mono text-sm text-terracotta">{u.ambassador_score}/100</div>
          </div>
          {!u.video_url && (
            <button onClick={() => onRequestVideo(u.id)} className="text-xs bg-forest text-white px-3 py-1.5 rounded hover:opacity-90 flex items-center gap-1" data-testid={`request-video-${u.public_id}`}>
              <VideoCamera size={12} /> Pedir video
            </button>
          )}
          {u.video_url && (
            <button onClick={() => onAdvance(u.id, "interview_pending", "moved by operator from video_received")} className="text-xs bg-terracotta text-white px-3 py-1.5 rounded hover:bg-terracotta-hover" data-testid={`to-interview-${u.public_id}`}>
              → Entrevista
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function AmbassadorCaptureModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", whatsapp: "+502",
    origin_country: "Guatemala", origin_department: "", origin_municipality: "",
  });
  const [scoreNow, setScoreNow] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/ambassador/capture", form);
      toast.success(`Postulante creado · ${data.public_id}`);
      if (scoreNow) {
        try {
          const r = await api.post(`/ambassador/score/${data.user_id}`, { message: `Soy de ${form.origin_municipality}, ${form.origin_department}, ${form.origin_country}.` });
          toast.success(`Scoring: ${r.data.score}/100 → ${r.data.stage}`);
        } catch (e) { /* */ }
      }
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-md p-6">
        <div className="section-label mb-1">Meta Lead · Ambassador</div>
        <h2 className="font-heading text-2xl mb-1">Capturar postulante a embajador</h2>
        <p className="text-xs text-carbon-500 mb-4">Crea UnifiedUser origin-side y lo coloca en stage <code className="font-mono">applicant</code>. Agenda primer mensaje IA y opcionalmente lo evalúa de inmediato.</p>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><span className="section-label">Nombre</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="amb-capture-name" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          <label className="block"><span className="section-label">WhatsApp</span>
            <input required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="amb-capture-whatsapp" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="section-label">País</span>
              <input required value={form.origin_country} onChange={(e) => setForm({ ...form, origin_country: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            <label className="block"><span className="section-label">Departamento</span>
              <input value={form.origin_department} onChange={(e) => setForm({ ...form, origin_department: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          </div>
          <label className="block"><span className="section-label">Municipio</span>
            <input value={form.origin_municipality} onChange={(e) => setForm({ ...form, origin_municipality: e.target.value })} data-testid="amb-capture-municipality" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          <label className="flex items-center gap-2 text-xs text-carbon-700">
            <input type="checkbox" checked={scoreNow} onChange={(e) => setScoreNow(e.target.checked)} /> Evaluar (score 0-100 + tipo A/B/C/D) inmediatamente
          </label>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            <button type="submit" disabled={busy} data-testid="amb-capture-submit" className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-60 flex items-center gap-1">
              {busy ? "Capturando..." : (<><ArrowRight size={14} /> Capturar</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
